import React from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import PauseCircleRoundedIcon from "@mui/icons-material/PauseCircleRounded";
import PriceChangeRoundedIcon from "@mui/icons-material/PriceChangeRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import CardGiftcardRoundedIcon from "@mui/icons-material/CardGiftcardRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import { useNavigate } from "react-router-dom";
import { planBillingApi } from "../../api/planBilling";
import AffiliateProgramAdminCard from "../../components/dashboard/AffiliateProgramAdminCard";

const BRAND = {
  orange: "#F35A3A",
  dark: "#19242F",
  page: "#F6F7F9",
  border: "rgba(25,36,47,.10)",
  muted: "rgba(25,36,47,.62)",
};

function money(value, currency = "ARS") {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency,
    maximumFractionDigits: currency === "ARS" ? 0 : 2,
  }).format(Number(value || 0));
}

function dateLabel(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium" }).format(parsed);
}

function recurringRevenueLabel(metrics = {}) {
  const entries = Object.entries(metrics.monthlyRecurringByCurrency || {}).filter(
    ([, amount]) => Number(amount || 0) > 0,
  );
  if (!entries.length) {
    return money(metrics.monthlyRecurring || 0, metrics.revenueCurrency || "ARS");
  }
  return entries.map(([currency, amount]) => money(amount, currency)).join(" + ");
}

function statusMeta(status) {
  const map = {
    trial: { label: "Prueba", color: "warning" },
    pending: { label: "Pendiente", color: "warning" },
    active: { label: "Activa", color: "success" },
    past_due: { label: "Pago atrasado", color: "error" },
    paused: { label: "Pausada", color: "error" },
    cancelled: { label: "Cancelada", color: "default" },
    exempt: { label: "Bonificada", color: "success" },
    authorized: { label: "Autorizada", color: "success" },
    rejected: { label: "Rechazada", color: "error" },
  };
  return map[status] || { label: status || "Sin estado", color: "default" };
}

function Metric({ icon, label, value, helper }) {
  return (
    <Card elevation={0} sx={{ height: "100%", borderRadius: 4, border: `1px solid ${BRAND.border}`, boxShadow: "0 14px 34px rgba(25,36,47,.05)" }}>
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar variant="rounded" sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: "rgba(243,90,58,.10)", color: BRAND.orange }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 900, color: BRAND.muted }}>{label}</Typography>
            <Typography variant="h5" sx={{ mt: 0.25, fontWeight: 950, color: BRAND.dark }}>{value}</Typography>
            <Typography variant="body2" sx={{ mt: 0.6, color: BRAND.muted }}>{helper}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function ServiceEditor({ service, saving, onSave }) {
  const [editing, setEditing] = React.useState(false);
  const [form, setForm] = React.useState({
    name: service.name || "",
    description: service.description || "",
    monthlyPrice: service.monthlyPrice || 0,
    currency: service.currency || "ARS",
    features: (service.features || []).join("\n"),
    recommended: Boolean(service.recommended),
    active: service.active !== false,
  });

  React.useEffect(() => {
    setForm({
      name: service.name || "",
      description: service.description || "",
      monthlyPrice: service.monthlyPrice || 0,
      currency: service.currency || "ARS",
      features: (service.features || []).join("\n"),
      recommended: Boolean(service.recommended),
      active: service.active !== false,
    });
  }, [service]);

  const save = async () => {
    const ok = await onSave(service.code, {
      ...form,
      monthlyPrice: Number(form.monthlyPrice || 0),
      features: String(form.features || "")
        .split("\n")
        .map((item) => item.trim())
        .filter(Boolean),
    });
    if (ok) setEditing(false);
  };

  return (
    <Card elevation={0} sx={{ height: "100%", borderRadius: 4, border: service.recommended ? `2px solid ${BRAND.orange}` : `1px solid ${BRAND.border}`, boxShadow: "0 14px 34px rgba(25,36,47,.05)" }}>
      <CardContent sx={{ p: 3, height: "100%", "&:last-child": { pb: 3 } }}>
        <Stack spacing={2} sx={{ height: "100%" }}>
          <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
            <Box>
              <Typography variant="caption" sx={{ color: BRAND.orange, fontWeight: 950, textTransform: "uppercase" }}>{service.code}</Typography>
              <Typography variant="h6" sx={{ mt: 0.4, fontWeight: 950, color: BRAND.dark }}>{service.name}</Typography>
            </Box>
            <Stack direction="row" spacing={0.75}>
              <Chip size="small" label={service.active !== false ? "Disponible" : "Oculto"} color={service.active !== false ? "success" : "default"} />
              <Button size="small" startIcon={<EditRoundedIcon />} onClick={() => setEditing((value) => !value)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
                {editing ? "Cerrar" : "Editar"}
              </Button>
            </Stack>
          </Stack>

          {!editing ? (
            <>
              <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark }}>{money(service.monthlyPrice, service.currency)}<Typography component="span" variant="body2" sx={{ color: BRAND.muted }}> / mes</Typography></Typography>
              <Typography variant="body2" sx={{ color: BRAND.muted }}>{service.description}</Typography>
              <Divider />
              <Stack spacing={0.8} sx={{ flex: 1 }}>
                {(service.features || []).map((feature) => (
                  <Typography key={feature} variant="body2" sx={{ color: BRAND.dark }}>• {feature}</Typography>
                ))}
              </Stack>
              {(service.requiredServiceCodes || []).length ? (
                <Typography variant="caption" sx={{ color: BRAND.muted }}>
                  Requiere: {service.requiredServiceCodes.join(", ")}
                </Typography>
              ) : null}
            </>
          ) : (
            <Stack spacing={1.5} sx={{ flex: 1 }}>
              <TextField label="Nombre comercial" size="small" value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
              <TextField label="Descripción" size="small" multiline minRows={2} value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} />
              <Stack direction="row" spacing={1.25}>
                <TextField label="Precio mensual" size="small" type="number" fullWidth value={form.monthlyPrice} onChange={(event) => setForm((current) => ({ ...current, monthlyPrice: event.target.value }))} inputProps={{ min: 0, step: 1 }} />
                <TextField label="Moneda" size="small" value={form.currency} onChange={(event) => setForm((current) => ({ ...current, currency: event.target.value.toUpperCase() }))} sx={{ width: 110 }} />
              </Stack>
              <TextField label="Beneficios, uno por línea" size="small" multiline minRows={4} value={form.features} onChange={(event) => setForm((current) => ({ ...current, features: event.target.value }))} />
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Stack direction="row" spacing={0.6} alignItems="center">
                  <Switch checked={form.recommended} onChange={(event) => setForm((current) => ({ ...current, recommended: event.target.checked }))} />
                  <Typography variant="body2">Recomendado</Typography>
                </Stack>
                <Stack direction="row" spacing={0.6} alignItems="center">
                  <Switch checked={form.active} onChange={(event) => setForm((current) => ({ ...current, active: event.target.checked }))} />
                  <Typography variant="body2">Disponible</Typography>
                </Stack>
              </Stack>
              <Button disabled={saving} onClick={save} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />} variant="contained" sx={{ mt: "auto", bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 950, "&:hover": { bgcolor: "#DE4D30" } }}>
                Guardar servicio
              </Button>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}


function ProjectPricingDialog({ open, project, services, saving, onClose, onSave }) {
  const [overrides, setOverrides] = React.useState({});
  const [applyToActiveSubscription, setApplyToActiveSubscription] = React.useState(false);

  React.useEffect(() => {
    const current = project?.billingSummary?.servicePriceOverrides || {};
    const next = {};
    for (const service of services || []) {
      next[service.code] = Object.prototype.hasOwnProperty.call(current, service.code)
        ? String(current[service.code])
        : "";
    }
    setOverrides(next);
    setApplyToActiveSubscription(false);
  }, [project, services, open]);

  if (!project) return null;

  const summary = project.billingSummary || {};
  const selectedCodes = new Set(
    summary.pendingServiceCodes?.length
      ? summary.pendingServiceCodes
      : summary.serviceCodes || [],
  );
  const hasActiveRecurringSubscription = Boolean(
    summary.subscriptionId &&
      summary.status === "active" &&
      summary.subscriptionStatus === "authorized",
  );

  const parsedPrice = (service) => {
    const raw = String(overrides[service.code] ?? "").trim();
    if (raw === "") return Number(service.monthlyPrice || 0);
    const amount = Number(raw);
    return Number.isFinite(amount) && amount >= 0 ? amount : null;
  };

  const invalidCodes = (services || [])
    .filter((service) => parsedPrice(service) === null)
    .map((service) => service.code);
  const hasInvalidPrice = invalidCodes.length > 0;
  const customCount = Object.values(overrides).filter(
    (value) => String(value).trim() !== "",
  ).length;
  const currentTotal = Number(summary.monthlyAmount || 0);
  const estimatedTotal = (services || [])
    .filter((service) => selectedCodes.has(service.code))
    .reduce((total, service) => total + Number(parsedPrice(service) || 0), 0);

  const save = () => {
    if (hasInvalidPrice) return;
    const cleanOverrides = {};
    for (const service of services || []) {
      const raw = String(overrides[service.code] ?? "").trim();
      if (raw !== "") cleanOverrides[service.code] = Number(raw);
    }
    onSave(project.id || project._id || project.slug, {
      overrides: cleanOverrides,
      applyToActiveSubscription,
    });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
          Precios para {project.name}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: BRAND.muted }}>
          Cada valor se aplica solamente a este proyecto. Vacío usa el precio general; cero bonifica ese módulo.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: BRAND.page,
              border: `1px solid ${BRAND.border}`,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              spacing={2}
            >
              <Box>
                <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 900 }}>
                  MENSUALIDAD ACTUAL
                </Typography>
                <Typography variant="h6" sx={{ mt: 0.3, color: BRAND.dark, fontWeight: 950 }}>
                  {money(currentTotal, summary.currency || "ARS")}
                </Typography>
              </Box>
              <Box sx={{ textAlign: { sm: "right" } }}>
                <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 900 }}>
                  TOTAL ESTIMADO CON ESTOS PRECIOS
                </Typography>
                <Typography variant="h6" sx={{ mt: 0.3, color: BRAND.orange, fontWeight: 950 }}>
                  {money(estimatedTotal, summary.currency || "ARS")}
                </Typography>
              </Box>
            </Stack>
            <Typography variant="caption" sx={{ display: "block", mt: 1.2, color: BRAND.muted }}>
              El total estimado usa los módulos que el cliente tiene contratados actualmente.
            </Typography>
          </Paper>

          <Stack spacing={1.25}>
            {(services || []).map((service) => {
              const raw = overrides[service.code] ?? "";
              const hasOverride = String(raw).trim() !== "";
              const isSelected = selectedCodes.has(service.code);
              const invalid = invalidCodes.includes(service.code);
              return (
                <Paper
                  key={service.code}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    borderColor: invalid
                      ? "error.main"
                      : hasOverride
                        ? "rgba(243,90,58,.42)"
                        : BRAND.border,
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ sm: "center" }}
                    spacing={1.5}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{service.name}</Typography>
                        {isSelected ? <Chip size="small" label="Contratado" color="success" /> : null}
                        {hasOverride ? <Chip size="small" label="Precio particular" color="warning" /> : null}
                      </Stack>
                      <Typography variant="body2" sx={{ mt: 0.45, color: BRAND.muted }}>
                        General: {money(service.monthlyPrice, service.currency)} / mes
                      </Typography>
                    </Box>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={0.75}
                      alignItems={{ sm: "center" }}
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                      <TextField
                        label="Precio para este cliente"
                        type="number"
                        size="small"
                        value={raw}
                        error={invalid}
                        helperText={invalid ? "Ingresá cero o un importe positivo" : " "}
                        onChange={(event) =>
                          setOverrides((current) => ({ ...current, [service.code]: event.target.value }))
                        }
                        inputProps={{ min: 0, step: 1 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        sx={{ width: { xs: "100%", sm: 230 } }}
                      />
                      {hasOverride ? (
                        <Button
                          size="small"
                          onClick={() =>
                            setOverrides((current) => ({ ...current, [service.code]: "" }))
                          }
                          sx={{
                            minWidth: { sm: 118 },
                            textTransform: "none",
                            fontWeight: 900,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Usar general
                        </Button>
                      ) : null}
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>

          {hasActiveRecurringSubscription ? (
            <Alert severity="warning" sx={{ borderRadius: 3 }}>
              <FormControlLabel
                sx={{ m: 0, alignItems: "flex-start" }}
                control={
                  <Switch
                    checked={applyToActiveSubscription}
                    onChange={(event) => setApplyToActiveSubscription(event.target.checked)}
                  />
                }
                label={
                  <Box sx={{ pt: 0.6 }}>
                    <Typography sx={{ fontWeight: 900 }}>Actualizar ahora la mensualidad en Mercado Pago</Typography>
                    <Typography variant="body2">
                      Cambia el importe recurrente de los módulos ya contratados. Si lo dejás apagado, el precio queda guardado para el próximo cambio de plan.
                    </Typography>
                  </Box>
                }
              />
            </Alert>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              Los precios quedan guardados para este proyecto y se usarán cuando el cliente autorice o actualice su plan.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography variant="caption" sx={{ mr: "auto", color: BRAND.muted, fontWeight: 800 }}>
          {customCount} módulo(s) con precio particular
        </Typography>
        <Button disabled={saving} onClick={onClose} sx={{ textTransform: "none", fontWeight: 900 }}>
          Cancelar
        </Button>
        <Button
          disabled={saving || hasInvalidPrice}
          onClick={save}
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
          sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 950, "&:hover": { bgcolor: "#DE4D30" } }}
        >
          Guardar precios
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AdminSettlementDialog({ open, project, action, saving, onClose, onConfirm }) {
  const [periodMonths, setPeriodMonths] = React.useState(1);
  const [amount, setAmount] = React.useState("");
  const [notes, setNotes] = React.useState("");

  const summary = project?.billingSummary || {};
  const isExempt = action === "exempt";
  const monthlyAmount = Number(summary.monthlyAmount || 0);
  const expectedAmount = monthlyAmount * periodMonths;
  const hasProviderAuthorization = Boolean(
    summary.subscriptionId || summary.pendingSubscriptionId,
  );

  React.useEffect(() => {
    if (!open) return;
    setPeriodMonths(1);
    setAmount(String(Number(summary.monthlyAmount || 0)));
    setNotes("");
  }, [open, project, action, summary.monthlyAmount]);

  if (!project) return null;

  const submit = () => {
    if (isExempt) {
      onConfirm(project.id || project._id || project.slug, {
        action: "exempt",
        notes,
      });
      return;
    }

    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) return;
    onConfirm(project.id || project._id || project.slug, {
      action: "manual_payment",
      periodMonths,
      amount: parsedAmount,
      notes,
    });
  };

  const invalidAmount = !isExempt && (!Number.isFinite(Number(amount)) || Number(amount) <= 0);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Avatar
            variant="rounded"
            sx={{
              bgcolor: isExempt ? "rgba(46,125,50,.10)" : "rgba(243,90,58,.10)",
              color: isExempt ? "success.main" : BRAND.orange,
            }}
          >
            {isExempt ? <CardGiftcardRoundedIcon /> : <PaidRoundedIcon />}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 950, color: BRAND.dark }}>
              {isExempt ? "Bonificar servicio" : "Registrar pago manual"}
            </Typography>
            <Typography variant="body2" sx={{ color: BRAND.muted }}>
              {project.name}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.2}>
          {hasProviderAuthorization ? (
            <Alert severity="warning" sx={{ borderRadius: 3 }}>
              Para evitar un cobro doble, esta acción cancelará la autorización recurrente o pendiente de Mercado Pago antes de aplicar el cambio.
            </Alert>
          ) : null}

          {isExempt ? (
            <Alert severity="success" sx={{ borderRadius: 3 }}>
              El proyecto quedará activo sin cargo y sin fecha de vencimiento. El valor mensual configurado se conserva para que puedas volver a cobrarlo más adelante.
            </Alert>
          ) : (
            <>
              <TextField
                select
                label="Período cubierto"
                value={periodMonths}
                onChange={(event) => {
                  const nextMonths = Number(event.target.value);
                  setPeriodMonths(nextMonths);
                  setAmount(String(monthlyAmount * nextMonths));
                }}
              >
                <MenuItem value={1}>1 mes</MenuItem>
                <MenuItem value={3}>3 meses</MenuItem>
                <MenuItem value={6}>6 meses</MenuItem>
                <MenuItem value={12}>12 meses</MenuItem>
              </TextField>
              <TextField
                label="Monto recibido"
                type="number"
                value={amount}
                error={invalidAmount}
                helperText={
                  invalidAmount
                    ? "Ingresá un importe mayor a cero"
                    : `Importe esperado según los servicios: ${money(expectedAmount, summary.currency || "ARS")}`
                }
                onChange={(event) => setAmount(event.target.value)}
                inputProps={{ min: 0.01, step: 1 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              <Paper
                elevation={0}
                sx={{ p: 2, borderRadius: 3, bgcolor: BRAND.page, border: `1px solid ${BRAND.border}` }}
              >
                <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 900 }}>
                  NUEVO VENCIMIENTO
                </Typography>
                <Typography sx={{ mt: 0.4, fontWeight: 950, color: BRAND.dark }}>
                  Se calculará desde hoy o desde el final del período manual que todavía esté vigente.
                </Typography>
              </Paper>
            </>
          )}

          <TextField
            label="Nota administrativa"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            multiline
            minRows={2}
            inputProps={{ maxLength: 1000 }}
            placeholder={isExempt ? "Ej.: bonificación comercial" : "Ej.: transferencia recibida"}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button disabled={saving} onClick={onClose} sx={{ textTransform: "none", fontWeight: 900 }}>
          Cancelar
        </Button>
        <Button
          disabled={saving || invalidAmount}
          onClick={submit}
          variant="contained"
          startIcon={
            saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : isExempt ? (
              <CardGiftcardRoundedIcon />
            ) : (
              <PaidRoundedIcon />
            )
          }
          sx={{
            bgcolor: isExempt ? "success.main" : BRAND.orange,
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 950,
            "&:hover": { bgcolor: isExempt ? "success.dark" : "#DE4D30" },
          }}
        >
          {isExempt ? "Confirmar bonificación" : "Registrar pago"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function BillingPlansAdminPage() {
  const navigate = useNavigate();
  const [data, setData] = React.useState(null);
  const [query, setQuery] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [pricingProject, setPricingProject] = React.useState(null);
  const [settlement, setSettlement] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setData(await planBillingApi.adminOverview());
    } catch (err) {
      setError(err.message || "No se pudo cargar el billing");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const updateService = async (code, payload) => {
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const response = await planBillingApi.updateService(code, payload);
      setMessage(response?.message || `Servicio ${code} actualizado.`);
      await load();
      return true;
    } catch (err) {
      setError(err.message || "No se pudo guardar el servicio");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const updateProjectPricing = async (projectId, payload) => {
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const response = await planBillingApi.updateProjectPricing(projectId, payload);
      setMessage(response?.message || "Precios particulares actualizados.");
      setPricingProject(null);
      await load();
      return true;
    } catch (err) {
      setError(err.message || "No se pudieron guardar los precios particulares");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const settleProject = async (projectId, payload) => {
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const response = await planBillingApi.settleProject(projectId, payload);
      setMessage(response?.message || "Billing actualizado.");
      setSettlement(null);
      await load();
      return true;
    } catch (err) {
      setError(err.message || "No se pudo registrar la acción administrativa");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const normalizedQuery = query.trim().toLowerCase();
  const projects = (data?.projects || []).filter((project) => {
    if (!normalizedQuery) return true;
    const summary = project.billingSummary || {};
    return [
      project.name,
      project.slug,
      project.owner?.email,
      summary.status,
      ...(summary.selectedServices || []).map((service) => service.name),
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(normalizedQuery));
  });
  const pending = (data?.subscriptions || []).filter((subscription) => subscription.status === "pending");

  if (loading && !data) {
    return (
      <Box sx={{ minHeight: 440, display: "grid", placeItems: "center" }}>
        <Stack alignItems="center" spacing={1.5}>
          <CircularProgress sx={{ color: BRAND.orange }} />
          <Typography sx={{ color: BRAND.muted, fontWeight: 800 }}>Cargando suscripciones…</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Stack spacing={3} sx={{ pb: 4 }}>
      <Paper elevation={0} sx={{ position: "relative", overflow: "hidden", p: { xs: 2.5, md: 4 }, borderRadius: 5, color: "#fff", background: "linear-gradient(135deg,#19242F 0%,#2A4052 68%,#3B596D 100%)", boxShadow: "0 22px 55px rgba(25,36,47,.20)" }}>
        <Box sx={{ position: "absolute", width: 280, height: 280, borderRadius: "50%", right: -90, top: -150, bgcolor: "rgba(243,90,58,.28)" }} />
        <Stack position="relative" direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={3}>
          <Box>
            <Typography variant="overline" sx={{ fontWeight: 900, letterSpacing: ".14em", color: "rgba(255,255,255,.65)" }}>WEBPROFIT · ADMINISTRACIÓN</Typography>
            <Typography variant="h3" sx={{ mt: 0.4, fontWeight: 950, letterSpacing: "-.04em", fontSize: { xs: "2rem", md: "2.65rem" } }}>Servicios y suscripciones</Typography>
            <Typography sx={{ mt: 1, maxWidth: 760, color: "rgba(255,255,255,.72)" }}>
              Vos definís el precio de cada servicio. Cada cliente elige su combinación y Mercado Pago cobra una sola mensualidad recurrente.
            </Typography>
          </Box>
          <Button disabled={loading || saving} onClick={load} startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshRoundedIcon />} variant="outlined" sx={{ color: "#fff", borderColor: "rgba(255,255,255,.38)", borderRadius: 999, textTransform: "none", fontWeight: 900, "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,.08)" } }}>
            Actualizar
          </Button>
        </Stack>
      </Paper>

      {error ? <Alert severity="error" onClose={() => setError("")} sx={{ borderRadius: 3 }}>{error}</Alert> : null}
      {message ? <Alert severity="success" onClose={() => setMessage("")} sx={{ borderRadius: 3 }}>{message}</Alert> : null}
      {data && data.mercadoPagoConfigured === false ? (
        <Alert severity="warning" sx={{ borderRadius: 3 }}>
          Falta configurar <strong>WEBPROFIT_MERCADOPAGO_ACCESS_TOKEN</strong> en el backend. El catálogo se puede editar, pero los clientes no podrán autorizar la mensualidad.
        </Alert>
      ) : null}

      <Grid container spacing={2.25}>
        <Grid item xs={12} sm={6} xl={3}><Metric icon={<StorefrontRoundedIcon />} label="CLIENTES / PROYECTOS" value={data?.metrics?.projects || 0} helper="Proyectos registrados en WebProfit." /></Grid>
        <Grid item xs={12} sm={6} xl={3}><Metric icon={<CreditCardRoundedIcon />} label="INGRESO MENSUAL" value={recurringRevenueLabel(data?.metrics)} helper="Suscripciones activas y en reintento." /></Grid>
        <Grid item xs={12} sm={6} xl={3}><Metric icon={<AutorenewRoundedIcon />} label="RECURRENTES MP" value={data?.metrics?.recurring || 0} helper="Tarjetas autorizadas en Mercado Pago." /></Grid>
        <Grid item xs={12} sm={6} xl={3}><Metric icon={<PendingActionsRoundedIcon />} label="PENDIENTES" value={data?.metrics?.pending || 0} helper={`${data?.metrics?.paused || 0} servicio(s) pausado(s).`} /></Grid>
      </Grid>

      <AffiliateProgramAdminCard />

      <Box>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "flex-end" }} spacing={2} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Catálogo mensual</Typography>
            <Typography sx={{ mt: 0.5, color: BRAND.muted }}>
              Estos precios se aplican a nuevas autorizaciones y cuando un cliente confirma una actualización de servicios.
            </Typography>
          </Box>
          <Chip icon={<TuneRoundedIcon />} label={`${data?.services?.length || 0} servicios configurados`} sx={{ fontWeight: 900 }} />
        </Stack>
        <Grid container spacing={2.25}>
          {(data?.services || []).map((service) => (
            <Grid item xs={12} md={6} xl={4} key={service.code}>
              <ServiceEditor service={service} saving={saving} onSave={updateService} />
            </Grid>
          ))}
        </Grid>
      </Box>

      <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 4, border: `1px solid ${BRAND.border}`, boxShadow: "0 14px 34px rgba(25,36,47,.05)" }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={2}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Precios por cliente y suscripciones</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: BRAND.muted }}>
              Definí importes particulares por módulo. La selección de servicios y la tarjeta siguen siendo administradas por cada cliente.
            </Typography>
          </Box>
          <TextField size="small" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar cliente o servicio" InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment> }} sx={{ width: { xs: "100%", md: 330 } }} />
        </Stack>

        <TableContainer sx={{ mt: 2.5, borderRadius: 3, border: `1px solid ${BRAND.border}` }}>
          <Table sx={{ minWidth: 940 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: BRAND.page }}>
                <TableCell>Cliente</TableCell>
                <TableCell>Servicios contratados</TableCell>
                <TableCell>Suscripción</TableCell>
                <TableCell>Próximo cobro</TableCell>
                <TableCell align="right">Mensualidad</TableCell>
                <TableCell align="right">Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map((project) => {
                const summary = project.billingSummary || {};
                const meta = statusMeta(summary.status);
                const names = (summary.selectedServices || []).map((service) => service.name);
                return (
                  <TableRow key={project.id || project.slug} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{project.name}</Typography>
                      <Typography variant="caption" sx={{ color: BRAND.muted }}>{project.owner?.email || `/${project.slug}`}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                        {names.length ? names.map((name) => <Chip key={name} size="small" label={name} variant="outlined" />) : <Typography variant="body2" sx={{ color: BRAND.muted }}>Sin selección confirmada</Typography>}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.6} alignItems="flex-start">
                        <Chip size="small" label={meta.label} color={meta.color} sx={{ fontWeight: 850 }} />
                        {Object.keys(summary.servicePriceOverrides || {}).length ? (
                          <Chip size="small" label="Precio particular" variant="outlined" color="warning" />
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {summary.status === "exempt"
                        ? "Sin vencimiento"
                        : dateLabel(summary.nextPaymentDate || summary.currentPeriodEnd)}
                    </TableCell>
                    <TableCell align="right">
                      {summary.status === "exempt" ? (
                        <Stack spacing={0.15} alignItems="flex-end">
                          <Typography variant="caption" sx={{ color: BRAND.muted, textDecoration: "line-through" }}>
                            {money(summary.monthlyAmount, summary.currency)}
                          </Typography>
                          <Typography sx={{ fontWeight: 950, color: "success.main" }}>Sin cargo</Typography>
                        </Stack>
                      ) : (
                        <Typography sx={{ fontWeight: 950 }}>{money(summary.monthlyAmount, summary.currency)}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                        <Button
                          disabled={saving}
                          startIcon={<PaidRoundedIcon />}
                          onClick={() => setSettlement({ project, action: "manual_payment" })}
                          sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, color: "success.dark" }}
                        >
                          Registrar pago
                        </Button>
                        <Button
                          disabled={saving}
                          startIcon={<CardGiftcardRoundedIcon />}
                          onClick={() => setSettlement({ project, action: "exempt" })}
                          sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, color: "warning.dark" }}
                        >
                          Bonificar
                        </Button>
                        <Button
                          startIcon={<PriceChangeRoundedIcon />}
                          onClick={() => setPricingProject(project)}
                          sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, color: BRAND.dark }}
                        >
                          Personalizar precios
                        </Button>
                        <Button onClick={() => navigate(`/dashboard/proyectos/${project.slug}/billing`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, color: BRAND.orange }}>
                          Ver suscripción
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!projects.length ? (
                <TableRow><TableCell colSpan={6} sx={{ py: 5, textAlign: "center", color: BRAND.muted }}>No hay resultados.</TableCell></TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {pending.length ? (
        <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 4, border: "1px solid rgba(237,108,2,.18)", bgcolor: "rgba(237,108,2,.035)" }}>
          <Stack direction="row" spacing={1.2} alignItems="flex-start">
            <PauseCircleRoundedIcon sx={{ color: "warning.main" }} />
            <Box>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{pending.length} autorización(es) pendiente(s)</Typography>
              <Typography variant="body2" sx={{ mt: 0.4, color: BRAND.muted }}>
                No requieren aprobación manual: están esperando que el cliente complete la autorización de tarjeta en Mercado Pago.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      ) : null}

      <Alert severity="info" sx={{ borderRadius: 3 }}>
        Los pagos registrados por el administrador quedan en el historial como cobros manuales. Las bonificaciones dejan el servicio activo sin cargo. Ninguna de las dos acciones genera una factura fiscal.
      </Alert>

      <ProjectPricingDialog
        open={Boolean(pricingProject)}
        project={pricingProject}
        services={data?.services || []}
        saving={saving}
        onClose={() => setPricingProject(null)}
        onSave={updateProjectPricing}
      />

      <AdminSettlementDialog
        open={Boolean(settlement)}
        project={settlement?.project || null}
        action={settlement?.action || "manual_payment"}
        saving={saving}
        onClose={() => setSettlement(null)}
        onConfirm={settleProject}
      />
    </Stack>
  );
}
